import clsx from 'clsx';
import RiskBadge from './RiskBadge';

interface DenialRateResult {
  group: { label: string; category: string };
  applications: number;
  denials: number;
  denialRate: number;
  controlGroupDenialRate: number;
  denialRateRatio: number;
  pValue: number | null;
  testUsed: string;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  isStatisticallySignificant: boolean;
  sampleSizeWarning: boolean;
}

const RISK_COLORS = {
  LOW: 'bg-green-500',
  MEDIUM: 'bg-amber-500',
  HIGH: 'bg-red-500',
};

export default function DenialRateChart({ results }: { results: DenialRateResult[] }) {
  if (!results || results.length === 0) {
    return <p className="text-sm text-gray-500">No denial rate data available.</p>;
  }

  const maxRatio = Math.max(...results.map(r => r.denialRateRatio), 2.5);

  return (
    <div className="space-y-6">
      {/* Chart */}
      <div className="space-y-3">
        {results.map((r, i) => {
          const barWidth = Math.min((r.denialRateRatio / maxRatio) * 100, 100);
          return (
            <div key={i} className="flex items-center gap-3">
              <div className="w-48 text-sm text-gray-700 truncate text-right flex-shrink-0">
                {r.group.label}
              </div>
              <div className="flex-1 relative">
                <div className="h-7 bg-gray-100 rounded-md relative overflow-hidden">
                  <div
                    className={clsx('h-full rounded-md transition-all', RISK_COLORS[r.riskLevel])}
                    style={{ width: `${barWidth}%` }}
                  />
                  {/* 1.5x threshold line */}
                  <div
                    className="absolute top-0 bottom-0 border-l-2 border-dashed border-amber-400"
                    style={{ left: `${Math.min((1.5 / maxRatio) * 100, 100)}%` }}
                    title="1.5x threshold"
                  />
                  {/* 2.0x threshold line */}
                  <div
                    className="absolute top-0 bottom-0 border-l-2 border-dashed border-red-400"
                    style={{ left: `${Math.min((2.0 / maxRatio) * 100, 100)}%` }}
                    title="2.0x threshold"
                  />
                </div>
              </div>
              <div className="w-32 text-xs text-gray-500 flex-shrink-0">
                <span className="font-semibold text-gray-800">{r.denialRateRatio.toFixed(2)}x</span>
                {r.sampleSizeWarning && <span className="ml-1 text-amber-500" title="Small sample size">⚠</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-4 border-t-2 border-dashed border-amber-400" /> 1.5x threshold
        </span>
        <span className="flex items-center gap-1">
          <span className="w-4 border-t-2 border-dashed border-red-400" /> 2.0x threshold
        </span>
      </div>

      {/* Detail table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b">
              <th className="py-2 pr-4">Group</th>
              <th className="py-2 pr-4">Apps</th>
              <th className="py-2 pr-4">Denials</th>
              <th className="py-2 pr-4">Rate</th>
              <th className="py-2 pr-4">Control Rate</th>
              <th className="py-2 pr-4">Ratio</th>
              <th className="py-2 pr-4">p-value</th>
              <th className="py-2 pr-4">Risk</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {results.map((r, i) => (
              <tr key={i}>
                <td className="py-2 pr-4 font-medium text-gray-900">{r.group.label}</td>
                <td className="py-2 pr-4 text-gray-600">{r.applications}</td>
                <td className="py-2 pr-4 text-gray-600">{r.denials}</td>
                <td className="py-2 pr-4 text-gray-600">{(r.denialRate * 100).toFixed(1)}%</td>
                <td className="py-2 pr-4 text-gray-600">{(r.controlGroupDenialRate * 100).toFixed(1)}%</td>
                <td className="py-2 pr-4 font-semibold">
                  {r.denialRateRatio.toFixed(2)}x
                  {r.sampleSizeWarning && <span className="ml-1 text-amber-500">⚠</span>}
                </td>
                <td className="py-2 pr-4 text-gray-600">
                  {r.pValue !== null ? r.pValue.toFixed(4) : 'N/A'}
                  {r.isStatisticallySignificant && <span className="ml-1 text-red-500">*</span>}
                </td>
                <td className="py-2 pr-4"><RiskBadge level={r.riskLevel} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
